/**
 * Circular 3D chart tooltip.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ChildProperty, Complex, Property, extend, remove, isNullOrUndefined } from '@syncfusion/ej2-base';
import { Border, Font, Location } from '../../common/model/base';
import { removeElement, stopTimer, withInBounds } from '../../common/utils/helper';
import { Tooltip as SVGTooltip } from '@syncfusion/ej2-svg-base';
import { tooltipRender } from '../../common/model/constants';
/**
 * Represents data for a 3D point in a circular 3D series.
 *
 * @private
 */
export class CircularChart3DPointData {
    /**
     * Initializes a new instance of the CircularChart3DPointData class.
     *
     * @param {CircularChart3DPoints} point - The 3D point in the circular series.
     * @param {CircularChart3DSeries} series - The circular series to which the point belongs.
     * @param {number} index - The index of the point in the series. Default is 0.
     */
    constructor(point, series, index = 0) {
        this.point = point;
        this.series = series;
        this.index = index;
    }
}
/**
 * Represents the tooltip settings for a circular 3D chart.
 *
 */
export class CircularChart3DTooltipSettings extends ChildProperty {
}
__decorate([
    Property(false)
], CircularChart3DTooltipSettings.prototype, "enable", void 0);
__decorate([
    Property(true)
], CircularChart3DTooltipSettings.prototype, "enableMarker", void 0);
__decorate([
    Property(null)
], CircularChart3DTooltipSettings.prototype, "fill", void 0);
__decorate([
    Property(null)
], CircularChart3DTooltipSettings.prototype, "header", void 0);
__decorate([
    Property(null)
], CircularChart3DTooltipSettings.prototype, "opacity", void 0);
__decorate([
    Complex({ fontFamily: null, size: '12px', fontStyle: 'Normal', fontWeight: null, color: null }, Font)
], CircularChart3DTooltipSettings.prototype, "textStyle", void 0);
__decorate([
    Property(null)
], CircularChart3DTooltipSettings.prototype, "format", void 0);
__decorate([
    Property(null)
], CircularChart3DTooltipSettings.prototype, "template", void 0);
__decorate([
    Property(false)
], CircularChart3DTooltipSettings.prototype, "enableAnimation", void 0);
__decorate([
    Property(300)
], CircularChart3DTooltipSettings.prototype, "duration", void 0);
__decorate([
    Property(700)
], CircularChart3DTooltipSettings.prototype, "fadeOutDuration", void 0);
__decorate([
    Property(false)
], CircularChart3DTooltipSettings.prototype, "enableTextWrap", void 0);
__decorate([
    Complex({ color: null, width: null }, Border)
], CircularChart3DTooltipSettings.prototype, "border", void 0);
__decorate([
    Complex({ x: null, y: null }, Location)
], CircularChart3DTooltipSettings.prototype, "location", void 0);
/**
 * The `CircularChart3DTooltip` module is used to render tooltips for a circular 3D chart.
 */
export class CircularChartTooltip3D extends ChildProperty {
    constructor() {
        super(...arguments);
        /** @private */
        this.currentPoints = [];
        /** @private */
        this.previousPoints = [];
        /** @private */
        this.tooltipRendered = false;
    }
    /**
     * Handles the mouse leave event for the circular 3D chart.
     *
     * @param {CircularChart3D} chart - The circular 3D chart instance.
     * @returns {void}
     */
    mouseLeaveHandler(chart) {
        this.control = chart;
        this.element = chart.element;
        if (!this.control.tooltip) {
            this.tooltipRendered = false;
            return;
        }
        this.removeTooltip(this.control.tooltip.fadeOutDuration);
    }
    /**
     * Handles the mouse up event for the circular 3D chart.
     *
     * @param {PointerEvent | TouchEvent} event - The mouse or touch event.
     * @param {CircularChart3D} chart - The circular 3D chart instance.
     * @returns {void}
     */
    mouseUpHandler(event, chart) {
        this.control = chart;
        this.element = chart.element;
        const swipeThreshold = 10;
        const isWithinSwipeThreshold = Math.abs(chart.mouseX - chart.cachedX) < swipeThreshold &&
            Math.abs(chart.mouseY - chart.cachedY) < swipeThreshold;
        if (this.control.tooltip.enable && !chart.rotateActivate && isWithinSwipeThreshold
            && withInBounds(this.control.mouseX, this.control.mouseY, this.control.initialClipRect) && this.control.isTouch) {
            this.tooltip(event);
            this.removeTooltip(2000);
            this.tooltipRendered = true;
        }
        else if (this.control.isTouch) {
            this.removeTooltip(0);
        }
    }
    /**
     * Handles the mouse move event for the circular 3D chart.
     *
     * @param {PointerEvent | TouchEvent} event - The mouse or touch event.
     * @param {CircularChart3D} chart - The circular 3D chart instance.
     * @returns {void}
     */
    mouseMoveHandler(event, chart) {
        this.control = chart;
        this.element = chart.element;
        if (!this.tooltipRendered) {
            if (this.control.tooltip.enable && !chart.rotateActivate &&
                withInBounds(this.control.mouseX, this.control.mouseY, this.control.initialClipRect)) {
                this.tooltip(event);
            }
            else {
                this.removeTooltip(0);
            }
        }
        this.tooltipRendered = false;
    }
    /**
     * Displays the tooltip for the circular 3D pie chart on pointer events or touch events.
     *
     * @param  {PointerEvent} event - The event triggering the tooltip display (pointer event or touch event).
     * @returns {void}
     */
    tooltip(event) {
        this.renderSeriesTooltip(this.control, this.getPieData(event, this.control));
    }
    /**
     * Gets the HTML element with the specified ID from the document.
     *
     * @param {string} id - The ID of the HTML element to retrieve.
     * @returns {HTMLElement} - The HTML element with the specified ID, or null if not found.
     */
    getElement(id) {
        return document.getElementById(id);
    }
    /**
     * Gets the tooltip element based on the visibility of the tooltip.
     *
     * @param {boolean} isTooltip - A flag indicating whether the tooltip is currently visible.
     * @returns {HTMLDivElement} - The tooltip element is returned, or null if the tooltip is not visible.
     */
    getTooltipElement(isTooltip) {
        this.header = (this.control.tooltip.header === null) ? '${series.name}' : (this.control.tooltip.header);
        this.formattedText = [];
        const tooltipDiv = document.getElementById(this.control.element.id + '_tooltip');
        if (!isTooltip && !tooltipDiv) {
            return this.createElement();
        }
        return null;
    }
    /**
     * Creates and returns an HTMLDivElement for the tooltip.
     *
     * @returns {HTMLDivElement} - The created HTMLDivElement for the tooltip.
     */
    createElement() {
        const tooltipDiv = document.createElement('div');
        tooltipDiv.id = this.element.id + '_tooltip';
        tooltipDiv.className = 'ejSVGTooltip';
        tooltipDiv.style.pointerEvents = 'none';
        tooltipDiv.style.position = 'absolute';
        tooltipDiv.style.zIndex = '1';
        return tooltipDiv;
    }
    /**
     * Renders the tooltip for a circular 3D series based on the provided point data.
     *
     * @param {CircularChart3D} chart - The circular 3D chart instance.
     * @param {CircularChart3DPointData} data - The CircularChart3D point data for which the tooltip will be rendered.
     * @returns {void}
     * @private
     */
    renderSeriesTooltip(chart, data) {
        const svgElement = this.getElement(this.element.id + '_tooltip_svg');
        const isTooltip = svgElement && parseInt(svgElement.getAttribute('opacity'), 10) > 0;
        const tooltipDiv = this.getTooltipElement(isTooltip);
        const isFirst = !isTooltip;
        this.template = chart.tooltip.template;
        this.currentPoints = [];
        if (data.point) {
            if (this.pushData(data, tooltipDiv)) {
                this.triggerTooltipRender(data, isFirst, this.getTooltipText(data, chart.tooltip), this.findHeader(data));
            }
        }
        else {
            if (!data.point && this.isRemove) {
                this.removeTooltip(this.control.tooltip.fadeOutDuration);
                this.isRemove = false;
            }
        }
    }
    /**
     * Removes the tooltip with a specified duration.
     *
     * @param {number} duration - The duration for the tooltip removal animation.
     * @returns {void}
     * @private
     */
    removeTooltip(duration) {
        const tooltipElement = this.getElement(this.element.id + '_tooltip');
        this.stopAnimation();
        if (tooltipElement && this.previousPoints.length > 0) {
            this.tooltipInterval = +setTimeout(() => {
                if (this.svgTooltip) {
                    this.svgTooltip.fadeOut();
                }
            }, duration);
        }
    }
    /**
     * Stops the animation by clearing the tooltip interval.
     *
     * @returns {void}
     */
    stopAnimation() {
        stopTimer(this.tooltipInterval);
    }
    /**
     * Pushes CircularChart3D point data to the currentPoints array and updates the tooltip div if tooltip are enabled for the series.
     *
     * @param {CircularChart3DPointData} data - The CircularChart3D point data to be pushed.
     * @param {HTMLDivElement} tooltipDiv - The tooltip div element to be updated if tooltip are enabled.
     * @returns {boolean} - A flag indicating whether the data was successfully pushed.
     */
    pushData(data, tooltipDiv) {
        if (data.series.enableTooltip) {
            this.currentPoints.push(data);
            this.stopAnimation();
            if (tooltipDiv && !document.getElementById(tooltipDiv.id)) {
                document.getElementById(this.element.id + '_Secondary_Element').appendChild(tooltipDiv);
            }
            return true;
        }
        return false;
    }
    /**
     * Triggers the rendering of a tooltip for a CircularChart3D point data.
     *
     * @param {CircularChart3DPointData} point - The CircularChart3D point data for which the tooltip will be rendered.
     * @param {boolean} isFirst - A flag indicating whether it is the first rendering of the tooltip.
     * @param {string} textCollection - The collection of text to be included in the tooltip.
     * @param {string} headerText - The header text for the tooltip.
     * @returns {void}
     */
    triggerTooltipRender(point, isFirst, textCollection, headerText) {
        const tooltip = this.control.tooltip;
        const argsData = {
            cancel: false, text: textCollection, point: point.point, textStyle: tooltip.textStyle,
            series: point.series, headerText: headerText, template: this.template,
            data: {
                pointX: point.point.x, pointY: point.point.y, seriesIndex: point.series.index,
                pointIndex: point.point.index, pointText: point.point.text, seriesName: point.series.name
            }
        };
        const padding = 10;
        const tooltipSuccess = (argsData) => {
            if (!argsData.cancel) {
                this.formattedText = this.formattedText.concat(argsData.text);
                this.text = this.formattedText;
                this.headerText = argsData.headerText;
                const location = {
                    x: (tooltip.location.x !== null) ? tooltip.location.x : this.control.mouseX,
                    y: (tooltip.location.y !== null) ? tooltip.location.y : this.control.mouseY - padding
                };
                this.createTooltip(this.control, isFirst, location, point.series.clipRect, point.point, 0, this.control.initialClipRect, null, point.point, this.template ? argsData.template : '');
            }
            else {
                this.removeHighlight();
                remove(this.getElement(this.element.id + '_tooltip'));
            }
            this.isRemove = true;
        };
        tooltipSuccess.bind(this, point);
        this.control.trigger(tooltipRender, argsData, tooltipSuccess);
    }
    /**
     * Gets the CircularChart3D point data associated with a pointer or touch event on the chart.
     *
     * @param {PointerEvent | TouchEvent} event - The pointer or touch event.
     * @param {CircularChart3D} chart - The circular 3D chart instance.
     * @returns {CircularChart3DPointData} - The CircularChart3D point data corresponding to the event.
     */
    getPieData(event, chart) {
        let point;
        const series = chart.visibleSeries[0];
        const element = event.target;
        if (element.id.indexOf('point') > -1 && element.id.indexOf('series') > -1) {
            const pointIndex = parseInt(element.id.split('point-')[1], 10);
            point = series.points[pointIndex];
        }
        else if (element.id.indexOf('-data-label-text') > -1 && series.dataLabel.position === 'Inside') {
            const index = parseInt(element.id.split('data-label-text-')[1], 10);
            point = series.points[index];
        }
        else if (element.id.indexOf('data-label-series') > -1 && series.dataLabel.position === 'Inside') {
            const index = parseInt(element.id.split('data-label-series-0-shape-')[1], 10);
            point = series.points[index];
        }
        if (point) {
            const pointData = { point: point, series: series, index: point.index };
            return pointData;
        }
        return new CircularChart3DPointData(null, null);
    }
    /**
     * Gets the tooltip text for a circular 3D point data based on the specified tooltip settings.
     *
     * @param {CircularChart3DPointData} data - The circularChart3D point data for which the tooltip text will be generated.
     * @param {CircularChart3DTooltipSettingsModel} tooltip - The tooltip settings to determine the format of the text.
     * @returns {string} - The generated tooltip text.
     */
    getTooltipText(data, tooltip) {
        const series = data.series;
        let format = tooltip.format ? tooltip.format : this.control.theme.indexOf('Tailwind3') > -1 ? '${point.x} : ${point.y}' : '${point.x} : <b>${point.y}</b>';
        format = this.control.useGroupingSeparator ? format.replace('${point.y}', '${point.separatorY}') : format;
        return this.parseTemplate(data.point, series, format);
    }
    /**
     * Finds the header for circular 3D point data.
     *
     * @param {CircularChart3DPointData} data - The circular 3D point data for which the header will be found.
     * @returns {string} - The found header string.
     */
    findHeader(data) {
        if (this.header === '') {
            return '';
        }
        this.header = this.parseTemplate(data.point, data.series, this.header);
        if (this.header.replace(/<b>/g, '').replace(/<\/b>/g, '').trim() !== '') {
            return this.header;
        }
        return '';
    }
    /**
     * Parses the tooltip template string and resolves all supported point and series tokens for a circular 3D chart.
     *
     * @param {CircularChart3DPoints} point - The circular 3D chart point used to resolve point-based tooltip tokens.
     * @param {CircularChart3DSeries} series - The circular 3D chart series used to resolve series-based tooltip tokens.
     * @param {string} format - The tooltip template format string.
     * @returns {string} - Returns the parsed tooltip text with resolved token values.
     * @private
     */
    parseTemplate(point, series, format) {
        const pointKeys = Object.keys(point);
        const seriesKeys = Object.keys(Object.getPrototypeOf(series));
        if (format.indexOf('${') !== -1) {
            let previous;
            do {
                previous = format;
                format = this.resolveTooltipTokens(format, point, series, pointKeys, seriesKeys);
            } while (format !== previous && format.indexOf('${') !== -1);
        }
        return format;
    }
    /**
     * Resolves tooltip tokens in the specified circular 3D chart template string.
     *
     * @param {string} input - The tooltip template string that contains tokens to resolve.
     * @param {CircularChart3DPoints} point - The circular 3D chart point used to resolve point-based tokens.
     * @param {CircularChart3DSeries} series - The circular 3D chart series used to resolve series-based tokens.
     * @param {string[]} pointKeys - The available point property keys.
     * @param {string[]} seriesKeys - The available series property keys.
     * @returns {string} - Returns the tooltip template string with supported tokens resolved.
     * @private
     */
    resolveTooltipTokens(input, point, series, pointKeys, seriesKeys) {
        return input.replace(/\$\{([^{}]+)\}/g, this.replaceTooltipToken.bind(this, point, series, pointKeys, seriesKeys));
    }
    /**
     * Replaces a single tooltip token with the corresponding circular 3D chart point or series value.
     *
     * @param {CircularChart3DPoints} point - The circular 3D chart point used to resolve point-based tokens.
     * @param {CircularChart3DSeries} series - The circular 3D chart series used to resolve series-based tokens.
     * @param {string[]} pointKeys - The available point property keys.
     * @param {string[]} seriesKeys - The available series property keys.
     * @param {string} match - The complete matched tooltip token text.
     * @param {string} token - The token content inside the tooltip expression.
     * @returns {string} - Returns the resolved tooltip token value, or the original token text when it cannot be resolved.
     * @private
     */
    replaceTooltipToken(point, series, pointKeys, seriesKeys, match, token) {
        const colonIndex = token.indexOf(':');
        let rawPath;
        let inlineFormat = '';
        if (colonIndex === -1) {
            rawPath = token;
        }
        else {
            rawPath = token.substring(0, colonIndex);
            inlineFormat = token.substring(colonIndex + 1).trim();
        }
        // Path should not contain leading, trailing, or internal whitespace.
        // Format can contain whitespace, for example: ${point.x:MMM dd}
        if (rawPath !== rawPath.trim() || /\s/.test(rawPath)) {
            return match;
        }
        const path = rawPath;
        let rawValue;
        let fallbackText = '';
        if (path.indexOf('point.') === 0) {
            const dataValue = path.slice(6);
            // Path does not exist, so keep original token text.
            if (pointKeys.indexOf(dataValue) === -1) {
                return match;
            }
            rawValue = point[dataValue];
            if (isNullOrUndefined(rawValue)) {
                return String(rawValue);
            }
            if (typeof rawValue === 'number' && (isNaN(rawValue) || !isFinite(rawValue))) {
                return isNaN(rawValue) ? 'NaN' : rawValue < 0 ? '-Infinity' : 'Infinity';
            }
            fallbackText = String(rawValue);
        }
        else if (path.indexOf('series.') === 0) {
            const dataValue = path.slice(7);
            // Path does not exist, so keep original token text.
            if (seriesKeys.indexOf(dataValue) === -1) {
                return match;
            }
            rawValue = series[dataValue];
            if (isNullOrUndefined(rawValue)) {
                return String(rawValue);
            }
            fallbackText = String(rawValue);
        }
        else {
            return match;
        }
        // No inline format, so return the normal template value directly.
        if (colonIndex === -1 || inlineFormat === '') {
            return fallbackText;
        }
        return this.getFormattedTooltipValue(rawValue, inlineFormat, fallbackText);
    }
    /**
     * Gets the formatted tooltip value by applying the specified inline format when it matches the value type.
     *
     * @param {any} rawValue - The raw point or series value to format.
     * @param {string} inlineFormat - The inline format string specified in the tooltip token.
     * @param {string} fallbackText - The fallback tooltip text returned when formatting cannot be applied.
     * @returns {string} - Returns the formatted tooltip value, or the fallback text when the format is not applicable.
     * @private
     */
    getFormattedTooltipValue(rawValue, inlineFormat, fallbackText) {
        // Date should be formatted only with date-like format.
        if (rawValue instanceof Date && this.control.intl && !this.isNumericFormat(inlineFormat)) {
            return this.control.intl.getDateFormat({ format: inlineFormat })(rawValue);
        }
        // Number should be formatted only with numeric format.
        if (typeof rawValue === 'number' &&
            this.control.intl &&
            this.isNumericFormat(inlineFormat)) {
            return this.control.intl.getNumberFormat({
                format: inlineFormat,
                useGrouping: this.control.useGroupingSeparator
            })(rawValue);
        }
        return fallbackText;
    }
    /**
     * Checks whether the specified inline format is a numeric format.
     *
     * @param {string} format - The inline format string to validate.
     * @returns {boolean} - Returns true when the format is numeric; otherwise, false.
     * @private
     */
    isNumericFormat(format) {
        format = format.trim();
        return /^[nNpPcCeE]\d*$/.test(format) || /[0#%]/.test(format);
    }
    /**
     * Creates a tooltip for a circularChart3D chart.
     *
     * @param {CircularChart3D} chart - The circular 3D chart instance.
     * @param {boolean} isFirst - A flag indicating whether it is the first tooltip.
     * @param {CircularChart3DLocation} location - The location where the tooltip will be positioned.
     * @param {CircularChart3DLocation} clipLocation - The clipping location for the tooltip.
     * @param {CircularChart3DPoints} point - The circular 3D point associated with the tooltip.
     * @param {number} offset - The offset for the tooltip.
     * @param {Rect} bounds - The bounds of the tooltip.
     * @param {CircularChart3DPointData[]} extraPoints - An array of additional CircularChart3DPointData for the tooltip.
     * @param {CircularChart3DPoints | CircularChart3DPoints[]} templatePoint - The template point or points for the tooltip.
     * @param {string | Function} customTemplate - A custom template for the tooltip, specified as a string or a function.
     * @returns {void}
     */
    createTooltip(chart, isFirst, location, clipLocation, point, offset, bounds, extraPoints = null, templatePoint = null, customTemplate) {
        const tooltipModule = chart.circularChartTooltip3DModule;
        if (!tooltipModule || location === null) { // For the tooltip enable is false.
            removeElement(this.control.element.id + '_tooltip');
            return;
        }
        if (isFirst) {
            this.svgTooltip = new SVGTooltip({
                opacity: chart.tooltip.opacity ? chart.tooltip.opacity : ((this.control.theme === 'Material3' || this.control.theme === 'Material3Dark' || this.control.theme.indexOf('Bootstrap5') > -1) ? 1 : 0.75),
                header: this.headerText,
                content: this.text,
                fill: chart.tooltip.fill,
                border: chart.tooltip.border,
                enableAnimation: chart.tooltip.enableAnimation,
                location: location,
                shared: false,
                crosshair: false,
                shapes: !chart.tooltip.enableMarker ? [] : ['Circle'],
                clipBounds: clipLocation,
                areaBounds: bounds,
                palette: this.findPalette(),
                template: customTemplate || this.template,
                data: templatePoint,
                theme: chart.theme,
                offset: offset,
                textStyle: chart.tooltip.textStyle,
                isNegative: false,
                inverted: false,
                arrowPadding: 0,
                availableSize: chart.availableSize,
                duration: this.control.tooltip.duration,
                isCanvas: false,
                isFixed: (this.control.tooltip.location.x !== null || this.control.tooltip.location.y !== null),
                isTextWrap: chart.tooltip.enableTextWrap,
                blazorTemplate: { name: 'Template', parent: this.control.tooltip },
                controlInstance: this.control,
                enableRTL: chart.enableRtl,
                controlName: 'Chart',
                allowHighlight: false,
                tooltipRender: () => {
                    tooltipModule.removeHighlight();
                    tooltipModule.highlightPoints();
                    tooltipModule.updatePreviousPoint(extraPoints);
                },
                animationComplete: (args) => {
                    if (args.tooltip.fadeOuted) {
                        tooltipModule.fadeOut();
                    }
                }
            });
            this.svgTooltip.appendTo(this.getElement(this.element.id + '_tooltip'));
        }
        else {
            if (this.svgTooltip) {
                this.svgTooltip.location = location;
                this.svgTooltip.content = this.text;
                this.svgTooltip.header = this.headerText;
                this.svgTooltip.offset = offset;
                this.svgTooltip.palette = this.findPalette();
                this.svgTooltip.shapes = !chart.tooltip.enableMarker ? [] : ['Circle'];
                this.svgTooltip.data = templatePoint;
                this.svgTooltip.template = this.template;
                this.svgTooltip.controlName = 'Chart';
                this.svgTooltip.crosshair = false;
                this.svgTooltip.textStyle = chart.tooltip.textStyle;
                this.svgTooltip.isNegative = false;
                this.svgTooltip.clipBounds = clipLocation;
                this.svgTooltip.arrowPadding = 0;
                this.svgTooltip.allowHighlight = false;
                this.svgTooltip.dataBind();
            }
        }
        if (this.control.isReact) {
            this.control.renderReactTemplates();
        }
    }
    /**
     * Highlights multiple points in a circular 3D chart series.
     * This method iterates through a collection of points (assuming they are represented by 'i') and applies the highlight effect to each point.
     *
     * @returns {void}
     */
    highlightPoints() {
        for (const item of this.currentPoints) {
            if (item.series.isRectSeries && item.series.category === 'Series') {
                this.highlightPoint(item.series, item.point.index, true);
            }
        }
    }
    /**
     * Removes the highlight from a previously highlighted point in a circular 3D chart series.
     *
     * @returns {void}
     */
    removeHighlight() {
        let item;
        for (let i = 0, len = this.previousPoints.length; i < len; i++) {
            item = this.previousPoints[i];
            if (item.series.isRectSeries) {
                if (item.series.visible) {
                    this.highlightPoint(item.series, item.point.index, false);
                }
                continue;
            }
        }
    }
    /**
     * Highlights or un highlights a specific point in a circular 3D chart series.
     *
     * @param {CircularChart3DSeries} series - The circular 3D series to which the point belongs.
     * @param {number} pointIndex - The index of the point to be highlighted or un highlighted.
     * @param {boolean} highlight - A flag indicating whether to highlight (true) or un highlight (false) the point.
     * @returns {void}
     */
    highlightPoint(series, pointIndex, highlight) {
        if ((this.control.circularChartHighlight3DModule && this.control.highlightMode === 'None') || !this.control.circularChartHighlight3DModule) {
            const elements = document.querySelectorAll(`[id*="region-series-0-point-${pointIndex}"]`);
            const pointElements = [];
            elements.forEach((pointElement) => {
                const elementIndex = parseInt(pointElement.id.split('point-')[1], 10);
                if (elementIndex === pointIndex) {
                    pointElements.push(pointElement);
                }
            });
            const datalabelElement = document.getElementById(this.control.element.id + '-svg-data-label-text-' + pointIndex);
            const connectorElement = document.getElementById(this.control.element.id + '-datalabel-series-0-connector-' + pointIndex);
            const shapeElement = document.getElementById(this.control.element.id + '-svg-data-label-series-0-shape-' + pointIndex);
            if (datalabelElement) {
                pointElements.push(datalabelElement);
            }
            if (connectorElement) {
                pointElements.push(connectorElement);
            }
            if (shapeElement) {
                pointElements.push(shapeElement);
            }
            const seriesElements = document.getElementById(this.element.id + '-svg-chart-3d').children;
            if (seriesElements) {
                for (const seriesElement of seriesElements) {
                    if (seriesElement.parentElement.id === this.control.groupElement.id) {
                        const selection = seriesElement.hasAttribute('class') ? seriesElement.getAttribute('class').indexOf('_selection_') === -1 : true;
                        seriesElement.setAttribute('opacity', (highlight && this.control.highlightColor !== 'transparent' && selection ? 0.2 : series.opacity).toString());
                    }
                }
            }
            if (pointElements) {
                pointElements.forEach((element) => {
                    if (element.parentElement.id === this.control.groupElement.id) {
                        element.setAttribute('opacity', (series.opacity).toString());
                    }
                });
            }
        }
    }
    /**
     * Fades out the tooltip associated with the provided CircularChart3DPointData.
     *
     * @returns {void}
     */
    fadeOut() {
        const svgElement = this.getElement(this.element.id + '_tooltip_svg');
        const isTooltip = (svgElement && parseInt(svgElement.getAttribute('opacity'), 10) > 0);
        if (!isTooltip) {
            this.currentPoints = [];
            this.removeHighlight();
            this.previousPoints = [];
            this.svgTooltip = null;
            this.control.trigger('animationComplete', {});
        }
    }
    /**
     * Updates the previous point with additional CircularChart3DPointData.
     *
     * @param {CircularChart3DPointData} extraPoints - An array of additional CircularChart3DPointData to update the previous point.
     * @returns {void}
     */
    updatePreviousPoint(extraPoints) {
        if (extraPoints) {
            this.currentPoints = this.currentPoints.concat(extraPoints);
        }
        this.previousPoints = extend([], this.currentPoints, null, true);
    }
    /**
     * Finds and returns an array of colors from the current points.
     *
     * @returns {string[]} - An array of color strings.
     */
    findPalette() {
        const colors = [];
        for (const data of this.currentPoints) {
            colors.push(data.point.color);
        }
        return colors;
    }
    /**
     * Gets the module name for the circular 3D tooltip.
     *
     * @returns {string} - The module name.
     */
    getModuleName() {
        return 'CircularChartTooltip3D';
    }
    /**
     * Destroys the circular 3D tooltip module.
     *
     * @returns {void}
     * @private
     */
    destroy() {
        /**
         * Destroy method calling here.
         */
    }
}
